import React, { FunctionComponent, useState, useEffect, useMemo } from 'react';
import styles from './ReactSelectAutocomplete.module.scss';
import { _t } from 'app/utils/i18n';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { handleAutocompleteField } from 'app/Requests';

import { Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
interface Props {
  moduleName?: string;
  onChange?: (id?: number | Array<number>, name?: string) => void;
  customFilters?: any;
  isMulti?: boolean;
  placeholder?: string;
  handleGetOpts?: (option: OptionType) => void;
  value?: Record<string, number> | Array<Record<string, number>> | Record<string, string>; // { label:string , value:number }
  icon?: JSX.Element;
  keyExtractor?: string;
  name?: string;
  label?: string;
  error?: any;
  withControl?: boolean;
  control?: any;
  id?: number;
  selectRef?: any;
  isEdit?: boolean;
  isCreatable?: boolean;
  hideValue?: boolean;
  extractedIds?: number[];
  validateInput?: boolean;
  isDisabled?: boolean;
  notParent?: boolean;
  resetMulti?: boolean;
  removeDoubleQuotes?: boolean;
  size?: string;
  customWidth?: string;
  additionalExtractor?: string;
  additionalName?: string;
  defaultInputValue?: string;
  useLabel?: boolean;
  onBlur?: () => void;
}

export type OptionType = {
  name: string;
  id: number;
};

const ReactSelectAutocomplete: FunctionComponent<Props> = ({
  moduleName,
  isMulti,
  customFilters = {},
  onChange,
  placeholder,
  value,
  handleGetOpts,
  icon,
  keyExtractor,
  name,
  label,
  error,
  withControl,
  control,
  id,
  selectRef,
  isEdit,
  isCreatable,
  hideValue,
  extractedIds,
  validateInput,
  isDisabled,
  notParent,
  resetMulti,
  removeDoubleQuotes,
  size,
  customWidth,
  additionalExtractor,
  defaultInputValue,
  additionalName,
  onBlur,
  useLabel,
}) => {
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentInputValue, setCurrentInputValue] = useState<any>(value);
  const handleAutocompleteFieldMutation = useMutation(handleAutocompleteField);
  const handleInputChange = (value: any, action = '') => {
    if (typeof value === 'object') {
      if (!hideValue) setCurrentInputValue(value);
      setCurrentQuery('');
    } else {
      if (['set-value', 'input-blur', 'menu-close'].includes(action)) {
        return;
      }
      !isMulti && setCurrentInputValue(null);
      setCurrentQuery(value);
    }

    // multiSelect: return object

    if (isMulti) {
      typeof value !== 'string' && onChange?.(value);
      hideValue && setCurrentInputValue(null);
      return;
    }
    // compare page
    if (handleGetOpts) {
      handleGetOpts({ id: value?.value, name: value?.label });
      setCurrentInputValue({});
      return;
    }
    if (additionalExtractor && typeof value === 'object') {
      onChange?.(
        typeof value === 'object' ? value?.[additionalExtractor] : value,
        additionalName,
      );
    }

    if (additionalExtractor && typeof value !== 'object') {
      onChange?.(undefined, additionalName);
    }

    // singleSelect: return key,label, or object
    if (keyExtractor) {
      onChange?.(typeof value === 'object' ? value?.[keyExtractor] : value);
    } else {
      onChange?.(value);
    }
  };
  const removeQuotes = (items: any) =>
    removeDoubleQuotes
      ? items.map((el: any) => ({
          ...el,
          label: el.label.replaceAll('"', ''),
          value: el.id,
        }))
      : items;

  const loadOptions = async (inputValue: string) => {
    // React autocomplete should not accept more than 150 chars
    if (!moduleName) return;
    if (inputValue.length <= 150) {
      const res = await handleAutocompleteFieldMutation.mutateAsync({
        moduleName,
        data: {
          prefix: inputValue,
          //dont change the next line
          is_parent: notParent ? !notParent : undefined,
          ...customFilters,
        },
      });

      let options = res.payload?.records
        ?.map((el: any) => ({
          label: el.label
            ? el.label
            : typeof el.name === 'string'
              ? el.name
              : el.name.original,
          value: el.id ? el.id : el.value,
          useLabel: useLabel,
          ...el,
        }))
        .filter((el: any) => el.value !== id);

      options = removeQuotes(options);
      // IN CASE OF COMPARE PAGES TO PREVENT THE ADDED ITEMS FROM APPEARING IN THE AUTOCOMPLETE DROPDOWN
      if (extractedIds) {
        options = options.filter((option: any) =>
          extractedIds.every((id: any) => id !== option.value),
        );
      }

      return options;
    }
  };

  useEffect(() => {
    if (value === null||value===undefined) {
      setCurrentInputValue(null);
      setCurrentQuery('');
    }
    if (value && currentInputValue!==value && typeof value !=='string' && typeof value !=='number') {
      setCurrentInputValue(value);
    }
  }, [value]);

  const controlledProps = ({ ref, onChangeForm }: any) => ({
    ref,
    className: 'autoCompleteInput',
    loadOptions,
    onChange: (option: any) => {
      const isArr = Array.isArray(option);
      isEdit
        ? onChangeForm(option)
        : onChangeForm(
            isArr ? option?.map((item) => item?.value) : option?.value,
          );
      handleInputChange(option);
    },

    isMulti,
    isClearable: true,
    components: { Option },
    placeholder: isDisabled
      ? ''
      : placeholder
        ? _t(`basic:${placeholder}`)
        : ` ${_t(`basic:searchBy_${moduleName}`)} ${
            moduleName === 'topic' ? 'Name' : 'Title'
          }`,
    value: currentInputValue,
    noOptionsMessage: ({ inputValue }: any) =>
      !inputValue ? _t('basic:type_to_search') : _t('basic:no_results'),
    loadingMessage: () => _t('basic:loading'),
    // onInputChange: (query, { action }) => {
    //   handleInputChange(query, action);
    // },
    inputValue: currentQuery ? currentQuery : undefined,
  });

  const unControlledProps: any = {
    className: `autoCompleteInput ${size} `,
    loadOptions,
    onChange: handleInputChange,
    isMulti,
    components: { Option },
    ref: selectRef,
    isClearable: true,
    closeMenuOnSelect: false,
    onSelectResetsInput: false,
    blurInputOnSelect: false,
    // TO DO: SEND FULL PLACEHOLDER FROM OUTSIDE
    placeholder: isDisabled
      ? ''
      : placeholder
        ? _t(`basic:${placeholder}`)
        : ` ${_t(`basic:searchBy_${moduleName}`)} ${
            moduleName === 'topic' ? 'Name' : 'Title'
          }`,
    value: currentInputValue,
    noOptionsMessage: ({ inputValue }: any) =>
      !inputValue ? _t('basic:type_to_search') : _t('basic:no_results'),
    loadingMessage: () => _t('basic:loading'),
    onInputChange: (query: any, { action }: any) =>
      handleInputChange(query, action),
    inputValue: currentQuery ? currentQuery : undefined,
    onBlur: () => {
      onBlur?.();
      const isId = name?.includes('ids') || name?.includes('id');
      if (currentQuery?.length !== 0 && isId) {
        setCurrentQuery('');

        handleInputChange(currentInputValue);
      }
    },
  };

  const createLabel = useMemo(
    () => ({
      formatCreateLabel: (inputText: string) =>
        `${_t('basic:create')} ${inputText}`,
      onKeyDown: validateInput
        ? (event: any) => {
            if (['#'].includes(event.key)) event.preventDefault();
          }
        : undefined,
    }),

    [],
  ) as any;
  const optionStyles = {
    option: (styles: any, { isFocused }: { isFocused: boolean }) => {
      return {
        ...styles,
        backgroundColor: isFocused && 'var(--secondaryColor)',
        color: isFocused ? '#fff' : 'var(--mainColor)',
      };
    },
  };
  return (
    <div
      className={styles.ReactSelectAutocomplete}
      data-testid="ReactSelectAutocomplete-testid"
    >
      {(!!label || !!error?.[name || '']) && (
        <div
          className={`${styles.label} ${
            (error?.[name || ''] ||
              (typeof error === 'string' && error?.length)) &&
            styles.labelError
          } ${isDisabled ? 'labelDisabled' : ''}`}
        >
          {label && <label>{label}</label>}
          {error && (
            <p data-testid={`error-testid`} id="error-testid">
              {typeof error === 'string'
                ? error
                : Array.isArray(error[name || ''])
                  ? error[name || '']?.filter((e: any) => !!e)?.[0]?.message ||
                    error[name || '']?.filter((e: any) => !!e)?.[0]?.value
                      ?.message
                  : error[name || '']?.message}
            </p>
          )}
        </div>
      )}
      <div
        style={{ width: customWidth }}
        className={`${styles.input} ${icon && styles.iconInput} ${
          isDisabled ? 'inputDisabled' : ''
        } ${
          (error?.[name || ''] ||
            (typeof error === 'string' && error?.length)) &&
          styles.inputError
        }`}
        id={`autocomplete-${name}`}
        data-testid={`autocomplete-${name}`}
      >
        <div className={styles.icon}>{icon}</div>
        {withControl ? (
          <Controller
            control={control}
            name={name || ''}
            render={({ field: { onChange, ref } }) =>
              isCreatable ? (
                <AsyncCreatableSelect
                  {...{
                    ...controlledProps({
                      ref,
                      onChangeForm: onChange,
                    }),
                    ...createLabel,
                  }}
                  isDisabled={isDisabled}
                  createOptionPosition="first"
                />
              ) : (
                <AsyncSelect
                  {...controlledProps({
                    ref,
                    onChangeForm: onChange,
                  })}
                  isDisabled={isDisabled}
                  className="autoComplete_selector" //This selector for testers
                />
              )
            }
          />
        ) : isCreatable ? (
          <AsyncCreatableSelect
            {...{
              ...unControlledProps,
              ...createLabel,
              onFocus: handleInputChange,
            }}
            isDisabled={isDisabled}
            defaultInputValue={defaultInputValue}
            createOptionPosition="first"
            className="autoComplete_selector" //This selector for testers
          />
        ) : (
          <AsyncSelect
            {...unControlledProps}
            isDisabled={isDisabled}
            onBlur={resetMulti ? () => setCurrentQuery('') : undefined}
            styles={optionStyles}
            className="autoComplete_selector" //This selector for testers
          />
        )}
      </div>
    </div>
  );
};

export default ReactSelectAutocomplete;

const Option = (props: any) => {
  return (
    <div
      style={{
        maxWidth: '100%',
      }}
    >
      <components.Option {...props}>
        {/* <Checkbox
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxOutlinedIcon fontSize="small" />}
          checked={props.isSelected}
        /> */}
        <label
          dir="auto"
          style={{
            display: 'block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {props.label}
        </label>
      </components.Option>
    </div>
  );
};
